import React, { useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import {
  deleteCoverLetter,
  editCoverLetter,
} from "../../../../../../../services/selfAssessment.service";
import Button from "../../../../../../../components/button";
import { ButtonSize, ButtonVariant } from "../../../../../../../components/button/types";

interface CoverLetter {
  id: number;
  coverLetter: string;
  createdAt: string;
  formalityLevel: number;
  language: string;
  status: string;
  title: string;
}

interface CoverLetterTableProps {
  coverLetters: CoverLetter[] | undefined;
  refetch: any;
  setExpandedRow: any;
  expandedRow: any
}

const CoverLetterTable: React.FC<CoverLetterTableProps> = ({
  coverLetters,
  refetch,
  setExpandedRow,
  expandedRow
}) => {
  const { t } = useTranslation("myArea");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editTitle, setEditTitle] = useState<string>("");
  const [selectedCoverLetterId, setSelectedCoverLetterId] = useState<
    number | null
  >(null);

  const toggleExpandRow = (index: number) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const openEditTitleModal = (coverLetter: CoverLetter) => {
    setSelectedCoverLetterId(coverLetter.id);
    setEditTitle(coverLetter.title);
    setIsEditModalOpen(true);
  };

  const editMutation = useMutation({
    mutationFn: ({ id, title }: { id: number; title: string }) =>
      editCoverLetter({ id, title }),
  });

  const handleEditTitleSubmit = () => {
    if (editTitle.trim() === "") {
      return;
    }
    if (selectedCoverLetterId !== null) {
      editMutation.mutate(
        { id: selectedCoverLetterId, title: editTitle },
        {
          onSuccess: () => {
            refetch();
            setIsEditModalOpen(false);
          },
        }
      );
    }
  };

  const formatCoverLetter = (text: string) => {
    if (!text) return "";
    return text.split("\n").map((line, index) => (
      <span className="" key={index}>
        {line}
        <br />
      </span>
    ));
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
  };
  const copyCoverLetter = (coverLetterText: string) => {
    navigator.clipboard.writeText(coverLetterText).then(() => {
      console.log("Cover letter text copied!");
    });
  };

  const deleteMutation = useMutation({
    mutationFn: ({ id }: { id: number }) => deleteCoverLetter({ id }),
  });
  const deleteCoverLetterText = (id: number) => {
    console.log("Deleting cover letter with ID:", id);
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  };

  return (
    <>
    <div className="mt-6">
      <div className="p-4 text-left bg-Grey-light-3 overflow-y-auto">
        {coverLetters && coverLetters.length > 0 ? (
          <table className="min-w-full table-auto">
            <thead>
              <tr className="border-b">
                <th className="px-4 py-2 text-left font-semibold text-gray-800 w-1/5">
                  {t("assessments.cover-letter.table-date")}
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-800 w-3/5">
                  {t("assessments.cover-letter.table-title")}
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-800 w-1/6">
                  {t("assessments.cover-letter.table-actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {coverLetters.map((coverLetter, index) => (
                <React.Fragment key={coverLetter.id}>
                  {/* Main Row */}
                  <tr
                    className="cursor-pointer"
                    onClick={() => toggleExpandRow(index)}
                  >
                    <td className="px-4 py-2 text-gray-700">
                      {new Date(coverLetter.createdAt).toLocaleString("de-DE", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </td>

                    <td className="px-4 py-2 text-gray-700">
                      {coverLetter.title}
                    </td>

                    <td className="px-4 py-2 flex gap-3 justify-start items-start">
                      <ContentCopyIcon
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyCoverLetter(coverLetter.coverLetter);
                        }}
                      />
                      <EditIcon
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditTitleModal(coverLetter);
                        }}
                      />
                      <DeleteIcon
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCoverLetterText(coverLetter.id);
                        }}
                      />
                    </td>
                  </tr>

                  {/* Expanded Row (Cover Letter under Title column) */}
                  {expandedRow === index && (
                    <tr>
                      <td></td>
                      <td className="px-4 py-2 text-gray-700 bg-gray-100 ">
                        {formatCoverLetter(coverLetter.coverLetter)}
                      </td>
                      <td></td>
                    </tr>
                  )}

                  <tr className="border-b">
                    <td colSpan={3}></td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500">
            {t("assessments.cover-letter.not-available")}
          </p>
        )}
      </div>
    </div>
     {isEditModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg lg:w-1/3 w-2/3">
            <h2 className="text-lg font-semibold mb-2">
              {t("assessments.cover-letter.edit-title")}
            </h2>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="border p-2 w-full mb-4"
            />
            <div className="flex justify-end space-x-2">
              <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                onClick={handleCancelEdit}
              >
                {t("assessments.cover-letter.popup-cancel")}
              </Button>
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SMALL}
                onClick={() => handleEditTitleSubmit()}
              >
                {t("assessments.cover-letter.popup-submit")}
              </Button>
            </div>
          </div>
        </div>
      )}
      </>
  );
};

export default CoverLetterTable;
